<template>
  <b-modal
    id="model-link-modal"
    ref="bModal"
    :title="$t('Vincular Modelos')"
    size="lg"
    cancel-variant="outline-secondary"
    :ok-title="$t('Vincular')"
    :cancel-title="$t('Cancelar')"
    @ok="handleOk"
    @hidden="resetModal"
    :ok-disabled="selectedModels.length === 0 || isLinking"
    no-close-on-backdrop
    ignore-enforce-focus-selector=".vs__dropdown-menu, .vs__dropdown-menu *"
  >
    <b-form @submit.prevent="handleSubmit">
      <b-row class="mb-2">
        <b-col cols="12" md="6">
          <b-form-group :label="$t('Buscar Modelo')" label-for="search-model">
            <b-form-input
              id="search-model"
              v-model="searchQuery"
              :placeholder="$t('Digite para buscar...')"
              @input="debounceSearch"
            />
          </b-form-group>
        </b-col>
        <b-col cols="12" md="6">
          <b-form-group :label="$t('Filtrar por Fabricante')" label-for="manufacturer-filter">
            <v-select
              id="manufacturer-filter"
              v-model="selectedManufacturer"
              :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
              :options="manufacturerOptions"
              :reduce="val => val.value"
              :clearable="true"
              :placeholder="$t('Todas as marcas')"
              @input="onManufacturerChange"
            />
          </b-form-group>
        </b-col>
      </b-row>

      <!-- Grid de modelos -->
      <b-table
        :items="models"
        :fields="columns"
        responsive
        show-empty
        :empty-text="$t('Nenhum modelo encontrado.')"
        selectable
        select-mode="multi"
        @row-selected="onRowSelected"
        :busy="isLoading"
        sticky-header="300px"
      >
        <template #table-busy>
          <div class="text-center text-primary my-2">
            <b-spinner class="align-middle"></b-spinner>
            <strong class="ml-1">{{ $t('Carregando...') }}</strong>
          </div>
        </template>
        <template #cell(selected)="{ rowSelected }">
          <template v-if="rowSelected">
            <feather-icon icon="CheckSquareIcon" size="18" class="text-primary" />
          </template>
          <template v-else>
            <feather-icon icon="SquareIcon" size="18" class="text-secondary" />
          </template>
        </template>
      </b-table>

      <!-- Paginação Modal -->
      <div class="d-flex justify-content-end mt-1">
        <b-pagination
          v-model="currentPage"
          :total-rows="totalItems"
          :per-page="perPage"
          size="sm"
          class="mb-0"
        />
      </div>
    </b-form>
  </b-modal>
</template>

<script>
import { BModal, BForm, BRow, BCol, BFormGroup, BFormInput, BTable, BPagination, BSpinner } from 'bootstrap-vue'
import vSelect from 'vue-select'
import store from '@/store'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'
import assetStoreModule from '@/views/main/asset/assetStoreModule'

export default {
  components: { BModal, BForm, BRow, BCol, BFormGroup, BFormInput, BTable, BPagination, BSpinner, vSelect },
  props: {
    dtcTableId: {
      type: String,
      required: true
    }
  },
  data() {
    return {
      searchQuery: '',
      models: [],
      selectedModels: [],
      isLoading: false,
      isLinking: false,
      currentPage: 1,
      perPage: 50,
      totalItems: 0,
      searchTimeout: null,
      columns: [
        { key: 'selected', label: '' },
        { key: 'assetManufacturerName', label: this.$t('Fabricante') },
        { key: 'name', label: this.$t('Modelo') }
      ],
      selectedManufacturer: null,
      manufacturerOptions: []
    }
  },
  created() {
    const ASSET_APP_STORE_MODULE_NAME = 'app-asset'
    if (!store.hasModule(ASSET_APP_STORE_MODULE_NAME)) {
      store.registerModule(ASSET_APP_STORE_MODULE_NAME, assetStoreModule)
    }
    this.fetchManufacturers()
  },
  watch: {
    currentPage() {
      this.fetchModels()
    }
  },
  methods: {
    show() {
      this.$refs.bModal.show()
      this.fetchModels()
    },
    debounceSearch() {
      clearTimeout(this.searchTimeout)
      this.searchTimeout = setTimeout(() => {
        this.currentPage = 1
        this.fetchModels()
      }, 300)
    },
    fetchManufacturers() {
      store.dispatch('app-asset/fetchAssetsManufacture')
        .then(response => {
          this.manufacturerOptions = []
          if (response.data && response.data.data) {
            response.data.data.forEach(v => {
              this.manufacturerOptions.push({
                label: v.name,
                value: v.id
              })
            })
          }
        })
        .catch(console.error)
    },
    onManufacturerChange() {
      this.currentPage = 1
      this.fetchModels()
    },
    fetchModels() {
      this.isLoading = true
      store.dispatch('app-asset/fetchAssetsModel', {
        search: this.searchQuery,
        id: this.selectedManufacturer || '', // id representa ManufacturerId na API
        page: this.currentPage,
        perPage: this.perPage
      }).then(response => {
        this.models = response.data?.data || []
        this.totalItems = response.data?.totalItems || 0
      }).catch(() => {
        this.$toast({
          component: ToastificationContent,
          props: { title: this.$t('Erro ao carregar modelos'), icon: 'AlertTriangleIcon', variant: 'danger' }
        })
      }).finally(() => {
        this.isLoading = false
      })
    },
    onRowSelected(items) {
      this.selectedModels = items
    },
    handleOk(bvModalEvent) {
      bvModalEvent.preventDefault()
      this.handleSubmit()
    },
    handleSubmit() {
      if (this.selectedModels.length === 0) return

      this.isLinking = true
      
      const assetModelIds = this.selectedModels.map(model => model.id)

      store.dispatch('app-dtc/linkModel', { 
        dtcTableId: this.dtcTableId, 
        assetModelIds 
      })
        .then(() => {
          this.$toast({
            component: ToastificationContent,
            props: { title: this.$t('Modelos vinculados com sucesso'), icon: 'CheckIcon', variant: 'success' }
          })
          this.$emit('linked')
          this.$refs.bModal.hide()
        })
        .catch(() => {
          this.$toast({
            component: ToastificationContent,
            props: { title: this.$t('Erro ao vincular modelos'), icon: 'AlertTriangleIcon', variant: 'danger' }
          })
        })
        .finally(() => {
          this.isLinking = false
        })
    },
    resetModal() {
      this.searchQuery = ''
      this.selectedModels = []
      this.selectedManufacturer = null
      this.currentPage = 1
    }
  }
}
</script>

<style lang="scss">
@import '@core/scss/vue/libs/vue-select.scss';
</style>
